import React, { useEffect, useMemo, useState } from 'react';
import { formatAmount } from '../../lib/money';
import { Card, Spinner } from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useT } from '../../i18n';
import { budgetsApi } from './budgetsApi';
import './budgets.css';

/**
 * Compact budget status panel for the requisition / PO forms. Coding is per-line, so a
 * single document can touch several cost centres / GL accounts. This panel groups the
 * document's lines by their effective coding, calls the availability endpoint for each
 * distinct key, and shows allocated / used / remaining plus the projected remaining once
 * this document's amount is included. Warn-only: purely informational, never blocks.
 *
 * Pass either `groups` ([{ costCenter, glAccountCode, amount }]) or the legacy single
 * `costCenter` / `glAccountCode` / `documentAmount` props.
 */
export default function BudgetPanel({ propertyId, groups, costCenter, glAccountCode, documentAmount = 0, currency = 'USD' }) {
  const t = useT();
  // Normalise + dedupe coding groups, summing each key's projected amount.
  const norm = useMemo(() => {
    const raw = groups && groups.length
      ? groups
      : costCenter
        ? [{ costCenter, glAccountCode, amount: documentAmount }]
        : [];
    const map = new Map();
    for (const g of raw) {
      const cc = (g.costCenter || '').trim();
      if (!cc) continue;
      const gl = (g.glAccountCode || '').trim();
      const key = `${cc}::${gl}`;
      const prev = map.get(key) || { costCenter: cc, glAccountCode: gl, amount: 0 };
      prev.amount += Number(g.amount || 0);
      map.set(key, prev);
    }
    return [...map.values()];
  }, [groups, costCenter, glAccountCode, documentAmount]);

  const codingKey = norm.map((g) => `${g.costCenter}::${g.glAccountCode}`).join('|');

  const [loading, setLoading] = useState(false);
  const [availByKey, setAvailByKey] = useState({});

  useEffect(() => {
    let active = true;
    if (!norm.length) { setAvailByKey({}); return undefined; }
    setLoading(true);
    Promise.all(
      norm.map((g) =>
        budgetsApi
          .availability({ propertyId: propertyId || undefined, costCenter: g.costCenter, glAccountCode: g.glAccountCode || undefined })
          .then((res) => [`${g.costCenter}::${g.glAccountCode}`, res])
          .catch(() => [`${g.costCenter}::${g.glAccountCode}`, null]),
      ),
    )
      .then((entries) => { if (active) setAvailByKey(Object.fromEntries(entries)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId, codingKey]);

  if (!norm.length) return null;

  const multi = norm.length > 1;

  const renderGroup = (g) => {
    const key = `${g.costCenter}::${g.glAccountCode}`;
    const avail = availByKey[key];
    const cur = avail?.currency || currency;
    const money = (n) => `${cur} ${formatAmount(n)}`;
    const heading = (
      <span className="bud-panel__coding">
        {g.costCenter}{g.glAccountCode ? <> · GL {g.glAccountCode}</> : null}
      </span>
    );

    if (!avail?.hasBudget) {
      return (
        <div className="bud-panel__group" key={key}>
          {multi && heading}
          <p className="bud-panel__hint">
            {t('No budget defined for cost centre')} <strong>{g.costCenter}</strong>{g.glAccountCode ? <> / GL {g.glAccountCode}</> : null} {t('in the current period.')}
          </p>
        </div>
      );
    }

    const allocated = Number(avail.allocated || 0);
    const consumed = Number(avail.consumed || 0);
    const remaining = Number(avail.remaining || 0);
    const amount = Number(g.amount || 0);
    const projected = remaining - amount;
    const usedPct = allocated > 0 ? Math.min(100, (consumed / allocated) * 100) : 0;
    const projOver = projected < 0;
    const fillTone = consumed > allocated ? 'is-over' : usedPct >= 85 ? 'is-warning' : '';

    return (
      <div className="bud-panel__group" key={key}>
        <div className="bud-panel">
          <div className="bud-panel__row">
            <span className="bud-panel__label">
              {multi ? heading : avail.periodLabel}
              {multi && <span className="bud-panel__period"> · {avail.periodLabel}</span>}
            </span>
            <span className="bud-panel__value">{money(allocated)}</span>
          </div>
          <div className="bud-bar">
            <div className={`bud-bar__fill ${fillTone}`} style={{ width: `${usedPct}%` }} />
          </div>
          <div className="bud-panel__row">
            <span className="bud-panel__label">{t('Committed')}</span>
            <span className="bud-panel__value">{money(avail.committed)}</span>
          </div>
          <div className="bud-panel__row">
            <span className="bud-panel__label">{t('Actual (received)')}</span>
            <span className="bud-panel__value">{money(avail.actual)}</span>
          </div>
          <div className="bud-panel__row">
            <span className="bud-panel__label">{t('Pending requisitions')}</span>
            <span className="bud-panel__value">{money(avail.pending)}</span>
          </div>
          <div className="bud-panel__row">
            <span className="bud-panel__label">{t('Remaining')}</span>
            <span className={`bud-panel__value ${remaining < 0 ? 'bud-panel__value--over' : ''}`}>{money(remaining)}</span>
          </div>
          {amount > 0 && (
            <div className="bud-panel__row">
              <span className="bud-panel__label">{t('After this document')}</span>
              <span className={`bud-panel__value ${projOver ? 'bud-panel__value--over' : ''}`}>{money(projected)}</span>
            </div>
          )}
          {projOver && (
            <p className="bud-panel__hint" style={{ color: 'var(--color-danger)' }}>
              {t('This document would exceed the budget by {amount}. Submission is still allowed.', { amount: money(Math.abs(projected)) })}
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <Card>
      <Card.Header>
        <Card.Title>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <Icon.Wallet size={16} /> {t('Budget')}
          </span>
        </Card.Title>
      </Card.Header>
      <Card.Body>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-3)' }}><Spinner size={18} /></div>
        ) : (
          <div className="bud-panel__groups">{norm.map(renderGroup)}</div>
        )}
      </Card.Body>
    </Card>
  );
}
